import './FeedManagement.scss';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Intent, Size } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner';
import { useSetError } from '../../redux/modals';
import { awaitJSON, DagLambdaFetch } from '../../utils/fetch-utils';

const STATUS = {
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED'
} as const;

interface JobStatus {
  status: (typeof STATUS)[keyof typeof STATUS];
  lastmodified?: string;
}

export const FeedManagement = (): ReactElement => {
  const setError = useSetError();
  const [fetching, setFetching] = useState(true);
  const [channableSyncStatus, setChannableSyncStatus] = useState<JobStatus | undefined>();
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);

  useEffect(() => {
    const fetchChannableSyncStatus = async () => {
      try {
        const searchResults = await DagLambdaFetch('/search?q=CHANNABLE-SYNC-all');
        const parsedResult = await awaitJSON(searchResults);

        if (parsedResult.length > 0) {
          setChannableSyncStatus(parsedResult[parsedResult.length - 1]);
        }
      } catch (e) {
        setError({
          message: `Failed to fetch ingestion statuses. Error: ${e.message}`,
          reportError: e
        });
      } finally {
        // finally, not the try — a failure must still clear `fetching` or the Spinner never
        // resolves.
        setFetching(false);
      }
    };

    if (!channableSyncStatus && fetching) {
      fetchChannableSyncStatus();
    }
  }, [channableSyncStatus, fetching, setError]);

  const triggerSync = useCallback(async () => {
    try {
      await DagLambdaFetch('/start_single_job', {
        method: 'POST',
        body: {
          dag: 'channable-sync',
          task: 'CHANNABLE-SYNC',
          task_name: 'CHANNABLE-SYNC-none'
        }
      });
      setShowModalConfirmation(true);
      setChannableSyncStatus({ status: STATUS.RUNNING }); // Reset status to trigger re-fetch
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
  }, [setError]);

  const ModalConfirmation = useMemo(
    () => (
      <AlertDialog
        intent={Intent.SUCCESS}
        show={showModalConfirmation}
        onHide={() => setShowModalConfirmation(false)}
        title='Channable Sync started successfully.'
        body={undefined}
        passthroughProps={{ 'data-testid': 'confirmation-dialog' }}
      />
    ),
    [showModalConfirmation]
  );

  const channableSyncInner = useMemo(() => {
    if (fetching) {
      return <Spinner />;
    }

    if (!channableSyncStatus) {
      return <div>No results to display.</div>;
    }

    return (
      <div className='channableSyncStatus'>
        {channableSyncStatus.status === STATUS.SUCCEEDED ? (
          <span className='channable-sync-success' data-testid='channable-sync-status'>
            Last completed: {channableSyncStatus.lastmodified}
          </span>
        ) : (
          <span className='channable-sync-success' data-testid='channable-sync-status'>
            Running
          </span>
        )}
        <Button
          size={Size.LG}
          disabled={channableSyncStatus.status === STATUS.RUNNING}
          onClick={triggerSync}
        >
          Run Channable Sync
        </Button>
      </div>
    );
  }, [channableSyncStatus, fetching, triggerSync]);

  return (
    <Page title='Feed Management' pageType='Feed Management'>
      <div className='channable-sync-container' data-testid='channable-sync-container'>
        Channable Sync
        {channableSyncInner}
      </div>
      {ModalConfirmation}
    </Page>
  );
};
